import { useDatoPerson } from '@src/stores-v2/StoreContext';
import { LogManager } from '@src/utility/Logger';
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

const logger = LogManager.getLogger('feature:showcase-param-sync');

/**
 * Workspace slug in the URL. Read on arrival by this hook and by
 * `useResolveOrgFromUrlParams`; written by Story Manager's deep links.
 */
export const SHOWCASE_PARAM = 'showcase';

/**
 * Honor a `?showcase=<slug>` deep link when the user already has a *different*
 * workspace selected — the case `useResolveOrgFromUrlParams` deliberately
 * leaves alone, since that hook only fills in a missing org.
 *
 * Applied at most once per mount. A continuous sync would fight the workspace
 * switcher: the param is one render behind a manual switch, so a live effect
 * would keep snapping the user back to the workspace they just left.
 *
 * A slug the user isn't a member of is ignored (the store's own setter
 * enforces membership), leaving them wherever they were.
 */
export function useShowcaseParamSync(): void {
  const datoPerson = useDatoPerson();
  const [searchParams] = useSearchParams();
  const slug = searchParams.get(SHOWCASE_PARAM);
  const applied = useRef(false);
  // Read during render so the observer host re-runs the effect once the
  // membership list arrives (it loads after mount).
  const organizations = datoPerson.organizations;

  useEffect(() => {
    if (applied.current || !slug) return;
    // Orgs load after mount; wait rather than deciding on an empty list.
    if (organizations.length === 0) return;
    applied.current = true;

    if (datoPerson.selectedOrganization?.slug === slug) return;
    const target = organizations.find(org => org.slug === slug);
    if (!target) {
      logger.warn('Ignoring showcase deep link — user is not a member', {
        slug,
      });
      return;
    }
    // Select after this effect flush, not inside it: a synchronous write here
    // left the Dashboard's dependent effects (the story load) un-run on the
    // next commit, so a deep link showed an empty folder (PLAT-11907).
    queueMicrotask(() => {
      datoPerson.selectedOrganization = target;
    });
  }, [slug, organizations, datoPerson]);
}
